/**
 * TEMPORARY development/testing aid — remove or flip DEV_OTP_NOTICE_ENABLED
 * once the SMS provider is connected.
 *
 * While SMS delivery is unavailable, the Auth service (development/test
 * only) echoes the generated code as `devCode` on the OTP request ack. This
 * component shows THAT value, verbatim, so sign-in can be tested end to end.
 * It never generates, derives or stores a code, and it changes nothing about
 * verification: the code is still submitted to the real verify endpoint.
 *
 * To remove: delete this file, the `devOtp` state and the three references
 * in SignInPage.tsx, and the `devOtp.*` message keys.
 */

import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Machine, Notice } from '@/ui/kit';
import type { OtpRequestAck } from '@/core/auth/api';

/** Single kill switch. */
export const DEV_OTP_NOTICE_ENABLED = true;

/** The response field the code is read from. Nothing else is consulted. */
export const DEV_OTP_FIELD = 'devCode' as const;

/** Returns the backend-supplied code exactly as received, or null. */
export function devOtpFromAck(ack: OtpRequestAck | null | undefined): string | null {
  if (!DEV_OTP_NOTICE_ENABLED || !ack) return null;
  const v = ack[DEV_OTP_FIELD];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function DevOtpNotice({ code }: { code: string | null }) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  if (!code) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable (insecure context) — the code is still visible */
    }
  };

  return (
    <Notice
      kind="warn"
      title={intl.formatMessage({ id: 'devOtp.title' })}
      action={
        <Button
          type="button"
          variant="quiet"
          onClick={() => void copy()}
          style={{ minBlockSize: 44 }}
        >
          {intl.formatMessage({ id: copied ? 'devOtp.copied' : 'devOtp.copy' })}
        </Button>
      }
    >
      <div>{intl.formatMessage({ id: 'devOtp.body' })}</div>
      <div
        style={{
          marginBlockStart: 6,
          fontSize: 22,
          fontWeight: 600,
          letterSpacing: '0.18em',
        }}
      >
        <Machine>
          <span data-testid="dev-otp-code">{code}</span>
        </Machine>
      </div>
    </Notice>
  );
}
